import { TimesheetSettingsType } from 'api/customer/types';
import { Timesheet, TimesheetTemplate, Travelsheet } from 'api/report/types';

export const calculateTotalReportedTimeAsNumber = (
    timesheet: Timesheet,
    setting: TimesheetSettingsType
): number => {
    let sum = 0;

    const days = timesheet.days ?? [];
    for (let i = 0; i < days.length; i++) {
        const day = days[i];

        if (setting === TimesheetSettingsType.NORMAL) {
            for (let j = 0; j < day.hours.length; j++) {
                const v = parseFloat(day.hours[j]) || 0;
                sum = (sum || 0) + v;
            }

            if (day.sickHours) {
                for (let j = 0; j < day.sickHours.length; j++) {
                    const v = parseFloat(day.sickHours[j]) || 0;
                    sum = (sum || 0) + v;
                }
            }
        } else if (
            setting === TimesheetSettingsType.START_STOP_TIME &&
            day.intervals
        ) {
            for (let j = 0; j < day.intervals.length; j++) {
                const interval = day.intervals[j];
                const start = interval.start.split(':');
                const stop = interval.stop.split(':');
                const startMinutes =
                    parseInt(start[0]) * 60 + parseInt(start[1]);
                let stopMinutes = parseInt(stop[0]) * 60 + parseInt(stop[1]);
                if (!startMinutes || !stopMinutes) {
                    continue;
                }
                if (stopMinutes < startMinutes) {
                    stopMinutes += 24 * 60;
                }
                const diff =
                    stopMinutes - startMinutes - interval.breakInMinutes;
                if (diff > 0) {
                    sum += diff / 60.0;
                }
            }
        }
    }

    return sum;
};

export const calculateTotalReportedTime = (
    timesheet: Timesheet,
    setting: TimesheetSettingsType
): string => {
    return formatDraftHours(
        calculateTotalReportedTimeAsNumber(timesheet, setting)
    );
};

export const calculateSumPerColumn = (
    timesheet: Timesheet,
    templateData: TimesheetTemplate
): Summary => {
    const sum: number[] = Array(templateData.subheader?.length ?? 0).fill(0);

    const days = timesheet.days ?? [];
    for (let i = 0; i < days.length; i++) {
        const day = days[i];

        for (let j = 0; j < day.hours.length; j++) {
            const v = parseFloat(day.hours[j]) || 0;
            sum[j] = (sum[j] || 0) + v;
        }

        if (day.sickHours) {
            for (let j = 0; j < day.sickHours.length; j++) {
                const v = parseFloat(day.sickHours[j]) || 0;
                sum[j] = (sum[j] || 0) + v;
            }
        }
    }

    return sum.map(v => formatDraftHours(v));
};

export const calculateTravelsheetColumnSum = (
    travelsheet: Travelsheet,
    index: number
): string => {
    let v = 0;
    for (const k in travelsheet) {
        if (isTravelsheetKeyType(k)) {
            const values = travelsheet[k];
            if (values) {
                v += values[index] ? parseFloat(values[index]) || 0 : 0;
            }
        }
    }

    return v?.toFixed(1) || '';
};

export type TravelsheetKeyType =
    | 'travel1'
    | 'travel2'
    | 'travel3'
    | 'travel4'
    | 'travel5'
    | 'travel6'
    | 'travel7';

const isTravelsheetKeyType = (key: string): key is TravelsheetKeyType => {
    const keys: Array<TravelsheetKeyType> = [
        'travel1',
        'travel2',
        'travel3',
        'travel4',
        'travel5',
        'travel6',
        'travel7',
    ];
    return keys.some(travelKey => travelKey === key);
};

// Keep the existing draft rounding, including values that round to 60 minutes.
function formatDraftHours(value: number): string {
    const hours = Math.floor(value);
    const minutes = Math.round((value * 60) % 60);
    return `${hours}h ${minutes}m (${value.toFixed(2)})`;
}

export type Summary = string[];
